import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Wizard } from "../../../../../../themes/Default/components/Wizard";
import { Step } from "../../../../../../themes/Default/components/Wizard/Step";
import { groupProgressManagement } from "../../../../../../hooks/api/GroupProgressManagement/GroupProgressManagement";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const AddProgressTypeOption = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const { groupProgressId } = useParams();
    const { groupProgressTypeId } = useParams();
    const [groupProgressName, setGroupProgressName] = useState("");
    const [attachedGroupProgressTypeOptions, setAttachedGroupProgressTypeOption] = useState([]);

    const { 
        useListGroupGroupProgressTypeOptionMutation,
        useAddGroupGroupProgressTypeOptionMutation,
        useDeleteGroupGroupProgressTypeOptionMutation,
        useViewGroupProgressMutation
    } = groupProgressManagement;

    const [listGroupProgressTypeOptions] = useListGroupGroupProgressTypeOptionMutation();
    const [addGroupProgressTypeOption] = useAddGroupGroupProgressTypeOptionMutation();
    const [deleteGroupProgressTypeOption] = useDeleteGroupGroupProgressTypeOptionMutation();
    const [viewGroupProgress] = useViewGroupProgressMutation();

    const tabs = [
        { title: t("plans.title"), onClick: () => gotoPlansTab() },
        { title: t("groupTypes.title"), onClick: () => gotoTypeTab() },
        { title: t("groups.progress"), isActive: true },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoTypeTab = () => {
        navigate("/settings/groups/types");
    };

    const gotoPlansTab = () => {
        navigate("/settings/groups/plans");
    };
    
    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };

    const gotoProgressStep = () => {
        navigate('/settings/groups/progress/'+groupProgressId+'/edit');
    };

    const gotoProgressTypeStep = () => {
        navigate('/settings/groups/progress/'+groupProgressId+'/type/add');
    };

    const gotoEdit = (settingsGroupProgressTypeOptionId) => {
        navigate("/settings/groups/progress/" + groupProgressId + "/type/" + groupProgressTypeId + "/options/" + settingsGroupProgressTypeOptionId + "/edit");
    };

    const onRemove = (optionId) => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteGroupProgressTypeOption({settingsGroupProgressTypeId: groupProgressTypeId, optionId}).then((result) => {
                            if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            }else{
                                toast.info(result?.data?.info?.responseMessage);
                                loadAttachedGroupProgressTypeOptions();
                            }
                        })
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addGroupProgressTypeOption({settingsGroupProgressTypeId: groupProgressTypeId, body: values}).then((result) => {            
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result?.error?.data?.error?.responseMessage);
            } else {
                toast.info(result?.data?.info?.responseMessage);
                loadAttachedGroupProgressTypeOptions();
            }
        });    
    };

    const loadAttachedGroupProgressTypeOptions = () => {
        listGroupProgressTypeOptions({settingsGroupProgressTypeId: groupProgressTypeId}).then((result) => {
            setAttachedGroupProgressTypeOption(result?.data?.records);
        });
    }

    const loadGroupProgressName = () => {
        viewGroupProgress(groupProgressId).then((result) => {
            setGroupProgressName(result?.data?.name);
        });
    }

    useEffect(() => {
        loadAttachedGroupProgressTypeOptions();
        loadGroupProgressName();
     }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/settings/groups/progress/"+groupProgressId+"/type/add"}/>
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupProgressName}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("Napredak")} />
                    <Link title={t('details')} isActive={true}/>
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t('Naziv kolone')} isChecked={true} onClick={gotoProgressStep}/>
                    <Step title={t('Odabir tipova grupa')} isChecked={true}  onClick={gotoProgressTypeStep}/>
                    <Step title={t('Dodavanje vrednosti')} isActive={true}  />
                </Wizard>
                <br/>
                <br/>
                <br/>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        name: "", 
                    }}
                    onSubmit={(values, { resetForm }) => {
                        onSaved(values);
                        resetForm();
                    }}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Input 
                                        name="name" 
                                        label={t('groups.name')} 
                                        value={values.name} 
                                        onChange={handleChange} 
                                        required={true} 
                                        error={formErrors?.name}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
                <br />
                <List>
                    <ListHeader>
                        <ListTitle 
                            title={t('Vrednosti za kolonu')}
                            subtitle="List of the group types options who are attached to this group progress type" 
                        />
                    </ListHeader>
                    <ListBody>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell><span>{t('Ime')}</span></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {attachedGroupProgressTypeOptions?.length > 0 &&
                            attachedGroupProgressTypeOptions?.map((attachedGroupProgressType, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableDataCell> 
                                            <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(attachedGroupProgressType.id)}></Button>
                                        </TableDataCell>
                                        <TableDataCell>{attachedGroupProgressType.name}</TableDataCell>
                                        <TableDataCell>
                                            <Button icon="fa-regular fa-trash-can" onClick={() => onRemove(attachedGroupProgressType.id)}></Button>
                                        </TableDataCell>
                                    </TableRow>
                                )
                        })}
                        </TableBody>
                    </ListBody>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default AddProgressTypeOption;